import { Component } from '@theme/component';

export default class VariantFilter extends Component {
  /** @type {string} */
  #activeFilter = 'all';

  connectedCallback() {
    super.connectedCallback();

    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', (event) => {
        queueMicrotask(() => {
          this.#resetFilterIfNeeded(event);
          this.#applyFilter();
        });
      });
    }

    this.#initDragScroll();
  }

  /**
   * Returns the parsed JSON data from the embedded script tag.
   * @returns {{ variantFilters: Record<string, string|null>, optionValueVariants: Record<string, string[]> }}
   */
  get filterData() {
    if (!this._filterData) {
      const script = this.querySelector('script[data-variant-filter-map]');
      this._filterData = script ? JSON.parse(script.textContent) : { variantFilters: {}, optionValueVariants: {} };
    }
    return this._filterData;
  }

  /**
   * Handles a filter button click via on:click delegation.
   * @param {Event} event
   */
  handleFilterClick(event) {
    const button = event.target.closest('.variant-filter__button');
    if (!button) return;

    const filterValue = button.dataset.filterValue;

    // Update active button styling
    for (const btn of this.querySelectorAll('.variant-filter__button')) {
      btn.classList.remove('variant-filter__button--active');
      btn.setAttribute('aria-pressed', 'false');
    }
    button.classList.add('variant-filter__button--active');
    button.setAttribute('aria-pressed', 'true');

    this.#activeFilter = filterValue;
    VariantFilter.#applyAllFilters(this.closest('.shopify-section'));
    this.#autoSelectIfNeeded();
  }

  /**
   * Checks whether a variant passes this filter instance.
   * @param {string} variantId
   * @returns {boolean}
   */
  passesFilter(variantId) {
    if (this.#activeFilter === 'all') return true;
    const filterName = this.filterData.variantFilters[variantId];
    return filterName === this.#activeFilter;
  }

  /**
   * Applies all variant-filter instances within a section (AND logic).
   * A variant must pass every active filter to remain visible.
   * @param {Element|null} section
   */
  static #applyAllFilters(section) {
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    const filters = /** @type {VariantFilter[]} */ (Array.from(section.querySelectorAll('variant-filter')));
    if (!filters.length) return;

    // Use the first filter's optionValueVariants map (all share the same product)
    const { optionValueVariants } = filters[0].filterData;

    const elements = variantPicker.querySelectorAll('[data-option-value-id]');
    for (const element of elements) {
      const optionValueId = element.dataset.optionValueId;
      const variantIds = optionValueVariants[optionValueId] || [];

      const isOption = element.tagName === 'OPTION';
      const label = isOption ? null : element.closest('label');
      const target = isOption ? element : label;

      if (!target) continue;

      // Show if ANY variant for this option value passes ALL active filters
      const passes = variantIds.some((id) => filters.every((f) => f.passesFilter(id)));

      if (isOption) {
        target.hidden = !passes;
      } else if (passes) {
        target.classList.remove('variant-filter--hidden');
      } else {
        target.classList.add('variant-filter--hidden');
      }
    }
  }

  /**
   * Resets the filter to "All" if the newly selected variant doesn't pass the active filter.
   * @param {CustomEvent} event
   */
  #resetFilterIfNeeded(event) {
    if (this.#activeFilter === 'all') return;

    const variantId = String(event.detail?.resource?.id ?? '');
    if (!variantId || this.passesFilter(variantId)) return;

    // Reset to "All"
    this.#activeFilter = 'all';
    for (const btn of this.querySelectorAll('.variant-filter__button')) {
      const isAll = btn.dataset.filterValue === 'all';
      btn.classList.toggle('variant-filter__button--active', isAll);
      btn.setAttribute('aria-pressed', String(isAll));
    }
  }

  /**
   * Instance wrapper so the variant:update listener can re-apply.
   */
  #applyFilter() {
    VariantFilter.#applyAllFilters(this.closest('.shopify-section'));
  }

  /**
   * Auto-selects the first visible variant when the current selection is filtered out.
   */
  #autoSelectIfNeeded() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    // Check radio inputs
    const checkedInput = variantPicker.querySelector('fieldset input:checked');
    if (checkedInput) {
      const label = checkedInput.closest('label');
      if (label && label.classList.contains('variant-filter--hidden')) {
        const firstVisible = variantPicker.querySelector(
          'fieldset label:not(.variant-filter--hidden) input[type="radio"]'
        );
        if (firstVisible) {
          firstVisible.click();
        }
      }
      return;
    }

    // Check select dropdowns
    const select = variantPicker.querySelector('select');
    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.hidden) {
        for (const option of select.options) {
          if (!option.hidden) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }
  }

  // ── Drag-to-scroll on the button row ────────────────────────────────────

  #initDragScroll() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return; // native touch scrolling handles this
      if (e.button !== 0) return;
      isDown = true;
      hasDragged = false;
      startX = e.clientX;
      scrollStart = scroller.scrollLeft;
      scroller.style.cursor = 'grabbing';
    });

    scroller.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) hasDragged = true;
      scroller.scrollLeft = scrollStart - dx;
    });

    const stopDrag = () => {
      if (!isDown) return;
      isDown = false;
      scroller.style.cursor = '';
    };

    scroller.addEventListener('pointerup', stopDrag);
    scroller.addEventListener('pointercancel', stopDrag);

    // Suppress button clicks that were actually drags
    scroller.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  // ── Scrollbar arrow handlers (delegated from slideshow-scrollbar arrows) ──

  /**
   * Scroll the filter buttons row to the left.
   */
  previous() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;
    scroller.scrollBy({ left: -scroller.clientWidth * 0.75, behavior: 'smooth' });
  }

  /**
   * Scroll the filter buttons row to the right.
   */
  next() {
    const scroller = this.querySelector('[ref="scroller"]');
    if (!scroller) return;
    scroller.scrollBy({ left: scroller.clientWidth * 0.75, behavior: 'smooth' });
  }
}

if (!customElements.get('variant-filter')) {
  customElements.define('variant-filter', VariantFilter);
}
